import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, ShieldCheck, Heart, Facebook, Instagram, Youtube } from "lucide-react";
import { BuddhaLogo } from "./BuddhaLogo";
import { useDonate } from "../context/DonateContext";
import { TRUST } from "../data/content";

export const Footer = () => {
  const { openDonate } = useDonate();
  return (
    <footer className="bg-[#0F2942] text-slate-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <BuddhaLogo className="h-14 w-14" />
              <p className="font-display font-bold text-white leading-tight">
                Lord Buddha Awareness<br />
                <span className="text-amber-400 text-sm font-semibold">& Development Foundation</span>
              </p>
            </div>
            <p className="text-sm leading-relaxed text-slate-400">
              A registered public charitable trust inspired by Lord Buddha's ideals of compassion,
              harmony and equality — working for education, health and social justice in rural Bihar.
            </p>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="hover:text-amber-400 transition-colors">Home</Link></li>
              <li><Link to="/about" className="hover:text-amber-400 transition-colors">About Us</Link></li>
              <li><Link to="/gallery" className="hover:text-amber-400 transition-colors">Gallery</Link></li>
              <li><Link to="/events" className="hover:text-amber-400 transition-colors">Events</Link></li>
              <li><Link to="/documents" className="hover:text-amber-400 transition-colors">Documents</Link></li>
              <li><Link to="/contact" className="hover:text-amber-400 transition-colors">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-amber-400 transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-amber-400 transition-colors">Terms & Conditions</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4">Reach Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2.5">
                <MapPin className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                <span>{TRUST.address}</span>
              </li>
              <li className="flex gap-2.5 items-center">
                <Mail className="h-5 w-5 text-emerald-400 shrink-0" />
                <a href={`mailto:${TRUST.email}`} className="hover:text-amber-400">{TRUST.email}</a>
              </li>
            </ul>
            <button
              onClick={openDonate}
              data-testid="footer-donate-btn"
              className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-all"
            >
              <Heart className="h-4 w-4 fill-white" /> Support Our Work
            </button>
            <div className="mt-6">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-3">Follow Us</p>
              <div className="flex items-center gap-3">
                <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook"
                  className="h-10 w-10 rounded-full bg-white/10 hover:bg-emerald-600 flex items-center justify-center transition-colors">
                  <Facebook className="h-5 w-5 text-white" />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram"
                  className="h-10 w-10 rounded-full bg-white/10 hover:bg-emerald-600 flex items-center justify-center transition-colors">
                  <Instagram className="h-5 w-5 text-white" />
                </a>
                <a href="https://youtube.com" target="_blank" rel="noreferrer" aria-label="YouTube" data-testid="social-youtube"
                  className="h-10 w-10 rounded-full bg-white/10 hover:bg-emerald-600 flex items-center justify-center transition-colors">
                  <Youtube className="h-5 w-5 text-white" />
                </a>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-white mb-4 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-400" /> Legal & Statutory
            </h4>
            <div className="text-sm space-y-2 bg-white/5 rounded-xl p-4 border border-white/10">
              <p><span className="text-slate-400">Registration:</span><br /><span data-testid="footer-registration-number" className="text-white font-medium">{TRUST.deedNo}</span></p>
              <p><span className="text-slate-400">Registered at:</span><br />{TRUST.registeredAt}</p>
              <p><span className="text-slate-400">PAN:</span> <span data-testid="footer-pan-number" className="text-white font-medium">{TRUST.pan}</span></p>
              <p><span className="text-slate-400">Income Tax URN:</span> <span className="text-white font-medium">{TRUST.urn}</span></p>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} {TRUST.name}. All rights reserved.</p>
          <p>President: {TRUST.president} · Registered {TRUST.regDate}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
